import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import Footer from '../../components/Layout/Footer';
import {
    EnvelopeIcon,
    LockClosedIcon,
    EyeIcon,
    EyeSlashIcon,
    BuildingOffice2Icon,
    CheckBadgeIcon,
    ChartBarIcon,
    UserGroupIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loginState, setLoginState] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
    const [errors, setErrors] = useState({});

    const { login } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name] || errors.form) {
            setErrors(prev => ({
                ...prev,
                [name]: '',
                form: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email) {
            newErrors.email = 'Email address is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        setLoginState('loading');
        try {
            const result = await login(formData.email, formData.password);
            if (result.success) {
                setLoginState('success');
                toast.success('Login Successful! Welcome back.', { id: 'auth-toast' });
                queryClient.invalidateQueries({ queryKey: ['sessions'] });
                queryClient.invalidateQueries({ queryKey: ['attendance'] });
                setTimeout(() => {
                    navigate('/dashboard');
                }, 400);
            } else if (result.approvalStatus === 'pending') {
                setLoginState('error');
                toast.error(result.error || 'Your account is pending approval.', { id: 'auth-toast' });
                setTimeout(() => {
                    navigate('/pending-approval');
                }, 800);
            } else if (result.approvalStatus === 'rejected') {
                setLoginState('error');
                toast.error(result.error || 'Your account has been rejected.', { id: 'auth-toast' });
                setErrors({ form: result.error || 'Account registration rejected. Contact administrator.' });
            } else if (result.isFirstLogin) {
                setLoginState('error');
                toast.error(result.error || 'Set your password using the invitation email link first.', { id: 'auth-toast' });
                setErrors({ form: result.error || 'Use the invitation email link to set your password before signing in.' });
            } else {
                setLoginState('error');
                toast.error(result.error || 'Invalid email or password', { id: 'auth-toast' });
                setErrors({ form: result.error || 'Invalid email or password. Please try again.' });
            }
        } catch (error) {
            console.error('Login error:', error);
            const msg = error.response?.data?.message || error.message || 'Invalid email or password';
            setLoginState('error');
            toast.error(msg, { id: 'auth-toast' });
            setErrors({ form: msg });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-slate-950 text-slate-100 font-sans selection:bg-indigo-500 selection:text-white">
            <div className="flex-1 flex w-full">
                {/* Left Enterprise Branding & Showcase Panel */}
                <div className="hidden lg:flex lg:w-1/2 relative bg-slate-900 overflow-hidden flex-col justify-between p-12 border-r border-slate-800/80">
                    {/* Ambient Glow Orbs */}
                    <div className="absolute top-0 -left-20 w-96 h-96 bg-indigo-600/30 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl pointer-events-none" />
                    <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-emerald-500/10 rounded-full blur-2xl pointer-events-none" />

                    {/* Top Branding Header */}
                    <div className="relative z-10 flex items-center gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-gradient-to-tr from-indigo-600 via-blue-600 to-sky-400 flex items-center justify-center shadow-lg shadow-indigo-500/25 border border-indigo-400/30">
                            <BuildingOffice2Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xl font-extrabold tracking-tight text-white font-sans">
                                Book<span className="text-indigo-400">My</span>Acre
                            </span>
                            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 font-semibold uppercase tracking-wider">
                                Enterprise CRM
                            </span>
                        </div>
                    </div>

                    {/* Center Hero Content */}
                    <div className="relative z-10 my-auto py-12 max-w-lg space-y-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/80 border border-slate-700/80 text-xs text-indigo-300 font-medium shadow-inner">
                            <span>Real Estate & Plotted Land Operating System</span>
                        </div>

                        <h1 className="text-4xl xl:text-5xl font-extrabold text-white tracking-tight leading-tight">
                            Scale your land sales with <span className="bg-gradient-to-r from-indigo-400 via-blue-400 to-sky-300 bg-clip-text text-transparent">Enterprise Intelligence</span>
                        </h1>

                        <p className="text-slate-400 text-base leading-relaxed">
                            Complete end-to-end sales automation, SLA lead reassignment, site visit management, and real-time executive analytics built for modern real estate developers.
                        </p>

                        {/* Feature Badges Grid */}
                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <div className="p-3.5 rounded-2xl bg-slate-800/50 border border-slate-800 flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-400">
                                    <UserGroupIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-white">Smart Lead Routing</div>
                                    <div className="text-[11px] text-slate-400">Automatic SLA reassignment</div>
                                </div>
                            </div>

                            <div className="p-3.5 rounded-2xl bg-slate-800/50 border border-slate-800 flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400">
                                    <ChartBarIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <div className="text-xs font-semibold text-white">Executive Analytics</div>
                                    <div className="text-[11px] text-slate-400">Real-time revenue metrics</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer Badge */}
                    <div className="relative z-10 flex items-center gap-2 text-xs text-slate-500">
                        <CheckBadgeIcon className="w-4 h-4 text-emerald-500" />
                        <span>Trusted by leading land development teams & agencies</span>
                    </div>
                </div>

                {/* Right Form Card Panel */}
                <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 bg-slate-950 relative overflow-hidden">
                    {/* Background Subtle Gradient */}
                    <div className="absolute -top-32 -right-32 w-80 h-80 bg-indigo-600/10 rounded-full blur-3xl pointer-events-none" />

                    <div className="w-full max-w-md space-y-8 glass-dark p-8 sm:p-10 rounded-3xl shadow-2xl border border-slate-800/80 relative z-10 fade-in-up">
                        {/* Mobile Header Logo */}
                        <div className="text-center space-y-2">
                            <div className="lg:hidden mx-auto h-12 w-12 rounded-2xl bg-gradient-to-tr from-indigo-600 to-blue-500 flex items-center justify-center shadow-lg shadow-indigo-500/30 mb-3">
                                <BuildingOffice2Icon className="h-6 w-6 text-white" />
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                                Welcome Back
                            </h2>
                            <p className="text-xs sm:text-sm text-slate-400">
                                Sign in to access your BookMyAcre CRM workspace
                            </p>
                        </div>

                        <form className="space-y-5" onSubmit={handleSubmit}>
                            {/* Email Address Input */}
                            <div className="space-y-1.5">
                                <label htmlFor="email" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                    Work Email Address
                                </label>
                                <div className="relative rounded-xl shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                        <EnvelopeIcon className="h-5 w-5" />
                                    </div>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        autoComplete="email"
                                        required
                                        disabled={loading || loginState === 'success'}
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="name@company.com"
                                        className={`w-full pl-11 pr-4 py-3 bg-slate-900/90 border text-sm text-white placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${errors.email ? 'border-red-500/80 focus:ring-red-500' : 'border-slate-800'
                                            }`}
                                    />
                                </div>
                                {errors.email && (
                                    <p className="text-xs text-red-400 font-medium pl-1">{errors.email}</p>
                                )}
                            </div>

                            {/* Password Input */}
                            <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                    <label htmlFor="password" className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
                                        Password
                                    </label>
                                    <Link
                                        to="/forgot-password"
                                        className="text-xs font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <div className="relative rounded-xl shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                                        <LockClosedIcon className="h-5 w-5" />
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type={showPassword ? 'text' : 'password'}
                                        autoComplete="current-password"
                                        required
                                        disabled={loading || loginState === 'success'}
                                        value={formData.password}
                                        onChange={handleChange}
                                        placeholder="••••••••••••"
                                        className={`w-full pl-11 pr-11 py-3 bg-slate-900/90 border text-sm text-white placeholder-slate-500 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${errors.password ? 'border-red-500/80 focus:ring-red-500' : 'border-slate-800'
                                            }`}
                                    />
                                    <button
                                        type="button"
                                        disabled={loading || loginState === 'success'}
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-200 transition-colors disabled:opacity-50"
                                    >
                                        {showPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-xs text-red-400 font-medium pl-1">{errors.password}</p>
                                )}
                            </div>

                            {/* Form Global Error Notice */}
                            {errors.form && (
                                <div className="p-3.5 rounded-xl bg-red-500/10 border border-red-500/30 text-xs text-red-400 font-medium flex items-center gap-2.5 animate-shake">
                                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 shrink-0" />
                                    <span>{errors.form}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading || loginState === 'success'}
                                className={`w-full py-3 px-4 rounded-xl text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-slate-950 shadow-lg transition-all duration-200 disabled:opacity-80 disabled:cursor-not-allowed flex items-center justify-center min-h-[46px] ${
                                    loginState === 'success'
                                        ? 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-600/30'
                                        : 'bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-700 hover:from-indigo-500 hover:to-blue-500 shadow-indigo-600/30 active:scale-[0.99]'
                                }`}
                            >
                                {loading && loginState === 'loading' ? (
                                    <div className="flex items-center gap-2">
                                        <LoadingSpinner size="small" />
                                        <span className="animate-pulse">Logging in...</span>
                                    </div>
                                ) : loginState === 'success' ? (
                                    <div className="flex items-center gap-2">
                                        <CheckCircleIcon className="w-5 h-5 text-white animate-bounce" />
                                        <span>Login Successful! Redirecting...</span>
                                    </div>
                                ) : (
                                    'Sign in to Dashboard'
                                )}
                            </button>

                            {/* Bottom Register Option */}
                            <div className="pt-4 text-center border-t border-slate-800/80 space-y-2">
                                <p className="text-xs text-slate-400">
                                    Need access for your executive or sales team?
                                </p>
                                <p className="text-xs">
                                    <Link
                                        to="/register"
                                        className="font-semibold text-indigo-400 hover:text-indigo-300 transition-colors"
                                    >
                                        Register New Account →
                                    </Link>
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Footer variant="app" />
        </div>
    );
};

export default Login;
